"use client";

import { useState } from "react";
import { Download } from "lucide-react";
import AuthModal from "@/app/components/AuthModal";
import { useAuth } from "@/app/providers/AuthProvider";
import type { TreasuryYieldPoint } from "@/lib/macro/us-treasury-yield";

function sourceForYear(year: number): string {
  if (year <= 1861) return "MeasuringWorth, US government securities";
  if (year <= 1869) return "Historical Statistics of the United States, Cj1192";
  if (year <= 1989) return "JST Macrohistory Database, USA long-term rate";
  return "US Treasury, daily par yield curve rates";
}

function escapeXml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

function buildExcelXml(rows: TreasuryYieldPoint[]): string {
  const header = [
    "Year",
    "Yield %",
    "Observation date",
    "Observation type",
    "Source",
  ]
    .map((label) => `<Cell><Data ss:Type="String">${label}</Data></Cell>`)
    .join("");

  const body = rows
    .map((row) => {
      const type =
        row.observationType === "latest_daily"
          ? "Latest daily"
          : "Annual average";
      return `<Row>
        <Cell><Data ss:Type="Number">${row.year}</Data></Cell>
        <Cell><Data ss:Type="Number">${row.yieldPct}</Data></Cell>
        <Cell><Data ss:Type="String">${row.observationDate}</Data></Cell>
        <Cell><Data ss:Type="String">${type}</Data></Cell>
        <Cell><Data ss:Type="String">${escapeXml(sourceForYear(row.year))}</Data></Cell>
      </Row>`;
    })
    .join("");

  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
  <Worksheet ss:Name="UST10Y">
    <Table>
      <Row>${header}</Row>
      ${body}
    </Table>
  </Worksheet>
</Workbook>`;
}

export default function TreasuryYieldExport({
  data,
}: {
  data: TreasuryYieldPoint[];
}) {
  const { user, authLoading } = useAuth();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "register">("register");

  function downloadExcel() {
    const xml = buildExcelXml(data);
    const blob = new Blob([xml], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const lastYear = data.at(-1)?.year ?? new Date().getUTCFullYear();
    link.href = url;
    link.download = `bastion-ust10y-yield-1800-${lastYear}.xls`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  function handleDownload() {
    if (authLoading) return;
    if (!user) {
      setAuthMode("register");
      setAuthModalOpen(true);
      return;
    }
    downloadExcel();
  }

  return (
    <>
      <button
        type="button"
        onClick={handleDownload}
        className="treasury-yield-export"
        aria-disabled={authLoading}
      >
        <Download size={13} strokeWidth={1.8} />
        Download Excel
      </button>
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        mode={authMode}
        onModeChange={setAuthMode}
        onAuthSuccess={() => {
          setAuthModalOpen(false);
          downloadExcel();
        }}
        customTitle="Create an account to download"
        customDescription="Register for free to download the complete UST10Y historical series."
      />
    </>
  );
}
